import React, { useState } from 'react';
import styles from './PartyPickerWidget.module.scss';

type ChildAge = { age: number };

type PartyPickerPayload = {
  rooms_max: number;
  adults_max: number;
  children_max: number;
  allow_child_ages: boolean;
  submitted_value?: {
    rooms: number;
    adults: number;
    children: ChildAge[];
  };
};

function StepRow({
  label,
  value,
  onDecrement,
  onIncrement,
}: {
  label: string;
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
}) {
  return (
    <div className={styles.stepRow}>
      <span className={styles.stepLabel}>{label}</span>
      <div className={styles.stepControls}>
        <button type="button" className={styles.stepButton} onClick={onDecrement} aria-label={`decrease ${label}`}>
          -
        </button>
        <span className={styles.stepValue}>{value}</span>
        <button type="button" className={styles.stepButton} onClick={onIncrement} aria-label={`increase ${label}`}>
          +
        </button>
      </div>
    </div>
  );
}

export function PartyPickerWidget({
  payload,
  onSubmit,
}: {
  payload: PartyPickerPayload;
  onSubmit: (value: { rooms: number; adults: number; children: ChildAge[] }) => void;
}) {
  const [rooms, setRooms] = useState(payload.submitted_value?.rooms ?? 1);
  const [adults, setAdults] = useState(payload.submitted_value?.adults ?? 2);
  const [children, setChildren] = useState<ChildAge[]>(payload.submitted_value?.children ?? []);

  const setChildCount = (count: number) => {
    setChildren((current) =>
      Array.from({ length: count }, (_, index) => current[index] ?? { age: 8 })
    );
  };

  return (
    <section className={styles.root} aria-label="Travellers">
      <h3 className={styles.title}>Travellers</h3>

      <StepRow
        label="Rooms"
        value={rooms}
        onDecrement={() => setRooms((v) => Math.max(1, v - 1))}
        onIncrement={() => setRooms((v) => Math.min(payload.rooms_max, v + 1))}
      />
      <StepRow
        label="Adults"
        value={adults}
        onDecrement={() => setAdults((v) => Math.max(1, v - 1))}
        onIncrement={() => setAdults((v) => Math.min(payload.adults_max, v + 1))}
      />
      <StepRow
        label="Children"
        value={children.length}
        onDecrement={() => setChildCount(Math.max(0, children.length - 1))}
        onIncrement={() => setChildCount(Math.min(payload.children_max, children.length + 1))}
      />

      {payload.allow_child_ages &&
        children.map((child, index) => (
          <div key={index} className={styles.childRow}>
            <span className={styles.childLabel}>Child {index + 1} age</span>
            <select
              className={styles.select}
              value={child.age}
              onChange={(e) =>
                setChildren((current) =>
                  current.map((item, i) => (i === index ? { age: Number(e.target.value) } : item))
                )
              }
            >
              {Array.from({ length: 18 }, (_, age) => (
                <option key={age} value={age}>
                  {age === 0 ? '< 1 year' : `${age} year`}
                </option>
              ))}
            </select>
          </div>
        ))}

      <div className={styles.footer}>
        <button type="button" className={styles.submitButton} onClick={() => onSubmit({ rooms, adults, children })}>
          Submit
        </button>
      </div>
    </section>
  );
}

